/**
 * Renderless foreground OS-permission sync (native app only).
 *
 * The user can change push permission any time via iOS/Android
 * Settings, and neither OS fires an app-level callback when they do.
 * We're stuck polling. This hook listens for the Capacitor
 * `appStateChange` event (fires when the app comes to the
 * foreground) and re-checks the OS permission. When the OS state
 * has drifted from what the server thinks:
 *
 *   OS granted but no server token -> silently register a fresh one.
 *   OS denied but server has a token -> revoke the token + record
 *     PushPromptState "system_denied" so no futile pushes fire and
 *     no re-prompt modals nag them.
 *
 * Only runs when there's a signed-in user + venue on the native
 * app. Fires on the initial mount AND on every foreground transition.
 * Runs at most once concurrently (in-flight ref) so a rapid
 * fore/back/fore doesn't stampede.
 *
 * Ties into existing subscribeToPush + unsubscribeFromPush +
 * POST /api/me/push-prompt.
 */
import { useEffect, useRef } from "react";
import { API_BASE } from "../lib/api";
import { isNativeApp } from "../lib/platform";
import {
  checkNativePushPermission,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/push";
import { hasVenueTokenIn } from "../lib/post-auth-push-register";
import {
  decideOsPermissionSync,
  type OsPermissionState,
} from "../lib/os-permission-sync";
import { recordPushPromptResponse } from "../lib/push-prompt-api";

type Props = {
  userId: string | null;
  venueId: string | null;
};

function foldPermission(
  raw: "granted" | "denied" | "prompt" | "prompt-with-rationale",
): OsPermissionState {
  if (raw === "granted") return "granted";
  if (raw === "denied") return "denied";
  return "prompt";
}

export function OsPermissionSyncHook({ userId, venueId }: Props) {
  const inFlightRef = useRef<boolean>(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!userId || !venueId) return;
    if (!isNativeApp()) return;

    const runSync = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const [osRaw, hasVenueToken] = await Promise.all([
          checkNativePushPermission(),
          fetchServerHasTokenForVenue(venueId),
        ]);
        const decision = decideOsPermissionSync({
          currentOsPermission: foldPermission(osRaw),
          serverHasToken: hasVenueToken,
        });
        if (decision.action === "no_action") return;

        if (decision.action === "revoke_token") {
          // Record system_denied FIRST, then delete the token. Peer-
          // review 2026-09-28 MEDIUM: the reverse order had a sub-
          // case where DELETE succeeded but POST failed, leaving the
          // server with the stale prior state ("granted") plus no
          // token; the signed-in modal's decision module would then
          // re-prompt on next launch, nagging a user who'd already
          // turned it off in Settings. This order self-heals: POST-
          // fail = bail (no changes, retry next foreground); POST-ok
          // + DELETE-fail = next foreground sees hasToken=true +
          // OS=denied, decides revoke_token again, retries DELETE.
          const recorded = await recordPushPromptResponse({
            venueId,
            response: "system_denied",
          });
          if (!recorded) {
            // eslint-disable-next-line no-console
            console.info("[os-sync] system_denied POST failed, will retry on next foreground");
            return;
          }
          try {
            await unsubscribeFromPush();
          } catch {
            // Best-effort; POST recorded the denial so no
            // signed-in-modal re-prompts fire, and next foreground
            // will retry the DELETE.
          }
          // eslint-disable-next-line no-console
          console.info("[os-sync] user disabled notifications in Settings; token revoked");
          return;
        }

        // reregister: OS was toggled back on. Silent (no OS dialog
        // since permission is already granted). If subscribeToPush
        // throws for any reason (transient network, plugin edge),
        // leave state untouched and retry on next foreground.
        try {
          await subscribeToPush(venueId);
          await recordPushPromptResponse({ venueId, response: "granted" });
          // eslint-disable-next-line no-console
          console.info("[os-sync] user re-enabled notifications in Settings; token registered");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.info("[os-sync] reregister failed, will retry on next foreground:", err);
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    // Fire once on mount (covers the "app cold-launched with new
    // permission state" case).
    void runSync();

    // Subscribe to foreground transitions. Capacitor's appStateChange
    // fires with { isActive: true } when the app returns to the
    // foreground.
    let unsubscribed = false;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const listener = await App.addListener("appStateChange", (state) => {
          if (state.isActive) void runSync();
        });
        if (unsubscribed) {
          listener.remove();
          return;
        }
        cleanupRef.current = () => {
          listener.remove();
        };
      } catch (err) {
        // Capacitor plugin missing or web runtime: fine, cold-launch
        // sync above already ran.
        // eslint-disable-next-line no-console
        console.info("[os-sync] Capacitor app listener unavailable:", err);
      }
    })();

    return () => {
      unsubscribed = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [userId, venueId]);

  return null;
}

async function fetchServerHasTokenForVenue(venueId: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/me/push`, { credentials: "include" });
    if (!res.ok) return false;
    const body = (await res.json()) as { tokens: Array<{ venueId: string | null }> };
    if (!Array.isArray(body.tokens)) return false;
    return hasVenueTokenIn(body.tokens, venueId);
  } catch {
    return false;
  }
}
